import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useUser } from '@/contexts/UserContext';
import { createUser } from '@/utils/api';
import { User } from 'lucide-react';
import BrandMark from '@/components/BrandMark';

const RoleSelectPage = () => {
  const navigate = useNavigate();
  const { login } = useUser();
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleGetStarted = () => {
    setShowForm(true);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!formData.name || !formData.email) {
      setError('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      const userData = {
        name: formData.name,
        email: formData.email,
        role: 'client'
      };

      const newUser = await createUser(userData);
      login(newUser);
      navigate('/client/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ivory-mist flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-12">
          <h1 className="font-display text-5xl font-bold tracking-tight text-obsidian-night mb-2">GNTL STRNGTH</h1>
          <p className="text-shadow-pine font-body">Presence Calibration</p>
        </div>

        {!showForm ? (
          <Card
            className="bg-white border-2 border-shadow-pine/20 hover:border-shadow-pine/50 transition-all cursor-pointer group hover:shadow-xl"
            onClick={handleGetStarted}
            data-testid="client-role-card"
          >
            <CardHeader>
              <div className="w-16 h-16 bg-shadow-pine/10 rounded-full flex items-center justify-center mb-4 mx-auto group-hover:bg-shadow-pine/20 transition-colors">
                <User className="w-8 h-8 text-shadow-pine" />
              </div>
              <CardTitle className="text-2xl font-display text-obsidian-night text-center">Begin Your Journey</CardTitle>
              <CardDescription className="text-shadow-pine font-body text-center">
                Calibrate your presence and unlock your potential
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm text-shadow-pine/80 font-body">
                <li>• Access daily calibration sessions</li>
                <li>• Follow guided movement patterns</li>
                <li>• Track your transformation</li>
                <li>• 5-minute post-workout journaling</li>
              </ul>
              <Button 
                className="w-full mt-6 bg-burnt-terra text-ivory-mist hover:bg-[#A54A2F] font-semibold"
              >
                Get Started
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-white border-burnt-terra/20" data-testid="user-info-form">
            <CardHeader>
              <CardTitle className="font-display text-obsidian-night">Create Your Account</CardTitle>
              <CardDescription className="text-shadow-pine font-body">
                Enter your details to begin
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-obsidian-night font-body">Full Name</Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="Enter your name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="bg-ivory-mist border-burnt-terra/30 focus:border-burnt-terra text-obsidian-night"
                    data-testid="name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-obsidian-night font-body">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="bg-ivory-mist border-burnt-terra/30 focus:border-burnt-terra text-obsidian-night"
                    data-testid="email-input"
                  />
                </div>

                {error && (
                  <div className="text-red-600 text-sm font-body">
                    {error}
                  </div>
                )}

                <div className="flex gap-2 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowForm(false)}
                    className="flex-1 border-shadow-pine/30 text-shadow-pine hover:bg-shadow-pine/10"
                  >
                    Back
                  </Button>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="flex-1 bg-burnt-terra text-ivory-mist hover:bg-[#A54A2F] font-semibold"
                    data-testid="submit-button"
                  >
                    {loading ? 'Creating...' : 'Continue'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}
      </div>
      <BrandMark />
    </div>
  );
};

export default RoleSelectPage;
